import { Injectable } from '@angular/core';

/**
 * Service to persist toolbar preferences across draft views
 */
@Injectable({
  providedIn: 'root'
})
export class ToolbarPreferencesService {
  private readonly STORAGE_KEY_PREFIX = 'toolbar_prefs_';

  // Cover toolbar preferences
  private coverToolbarExpanded: boolean | null = null;
  private coverToolbarPosition: string | null = null;
  private coverToolbarXY: { x: number, y: number } | null = null;

  // Interior toolbar preferences
  private interiorToolbarExpanded: boolean | null = null;
  private interiorToolbarPosition: string | null = null;
  private interiorToolbarXY: { x: number, y: number } | null = null;

  // Display toggle preferences (for interior)
  private showMarginCheck: boolean | null = null;
  private showRulers: boolean | null = null;
  private showDebugGrid: boolean | null = null;
  private showGutter: boolean | null = null;
  private showPageNumbers: boolean | null = null;

  constructor() {
    this.loadPreferences();
  }

  private loadPreferences(): void {
    try {
      this.coverToolbarExpanded = this.getFromStorage('cover_expanded');
      this.coverToolbarPosition = this.getFromStorage('cover_position');
      this.coverToolbarXY = this.getFromStorage('cover_xy');
      this.interiorToolbarExpanded = this.getFromStorage('interior_expanded');
      this.interiorToolbarPosition = this.getFromStorage('interior_position');
      this.interiorToolbarXY = this.getFromStorage('interior_xy');
      this.showMarginCheck = this.getFromStorage('show_margin_check');
      this.showRulers = this.getFromStorage('show_rulers');
      this.showDebugGrid = this.getFromStorage('show_debug_grid');
      this.showGutter = this.getFromStorage('show_gutter');
      this.showPageNumbers = this.getFromStorage('show_page_numbers');
    } catch (e) {
      console.warn('Failed to load toolbar preferences', e);
    }
  }

  private getFromStorage(key: string): any {
    const value = localStorage.getItem(this.STORAGE_KEY_PREFIX + key);
    return value ? JSON.parse(value) : null;
  }

  private saveToStorage(key: string, value: any): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_PREFIX + key, JSON.stringify(value));
    } catch (e) {
      console.warn('Failed to save toolbar preference', e);
    }
  }

  // Cover toolbar methods
  getCoverToolbarExpanded(defaultValue: boolean): boolean {
    return this.coverToolbarExpanded !== null ? this.coverToolbarExpanded : defaultValue;
  }

  setCoverToolbarExpanded(expanded: boolean): void {
    this.coverToolbarExpanded = expanded;
    this.saveToStorage('cover_expanded', expanded);
  }

  getCoverToolbarPosition(defaultValue: string): string {
    return this.coverToolbarPosition || defaultValue;
  }

  setCoverToolbarPosition(position: string): void {
    this.coverToolbarPosition = position;
    this.saveToStorage('cover_position', position);
  }

  getCoverToolbarXY(defaultValue: { x: number, y: number }): { x: number, y: number } {
    return this.coverToolbarXY || defaultValue;
  }

  setCoverToolbarXY(xy: { x: number, y: number }): void {
    this.coverToolbarXY = xy;
    this.saveToStorage('cover_xy', xy);
  }

  // Interior toolbar methods
  getInteriorToolbarExpanded(defaultValue: boolean): boolean {
    return this.interiorToolbarExpanded !== null ? this.interiorToolbarExpanded : defaultValue;
  }

  setInteriorToolbarExpanded(expanded: boolean): void {
    this.interiorToolbarExpanded = expanded;
    this.saveToStorage('interior_expanded', expanded);
  }

  getInteriorToolbarPosition(defaultValue: string): string {
    return this.interiorToolbarPosition || defaultValue;
  }

  setInteriorToolbarPosition(position: string): void {
    this.interiorToolbarPosition = position;
    this.saveToStorage('interior_position', position);
  }

  getInteriorToolbarXY(defaultValue: { x: number, y: number }): { x: number, y: number } {
    return this.interiorToolbarXY || defaultValue;
  }

  setInteriorToolbarXY(xy: { x: number, y: number }): void {
    this.interiorToolbarXY = xy;
    this.saveToStorage('interior_xy', xy);
  }

  // Display toggle methods
  getShowMarginCheck(defaultValue: boolean): boolean {
    return this.showMarginCheck !== null ? this.showMarginCheck : defaultValue;
  }

  setShowMarginCheck(show: boolean): void {
    this.showMarginCheck = show;
    this.saveToStorage('show_margin_check', show);
  }

  getShowRulers(defaultValue: boolean): boolean {
    return this.showRulers !== null ? this.showRulers : defaultValue;
  }

  setShowRulers(show: boolean): void {
    this.showRulers = show;
    this.saveToStorage('show_rulers', show);
  }

  getShowDebugGrid(defaultValue: boolean): boolean {
    return this.showDebugGrid !== null ? this.showDebugGrid : defaultValue;
  }

  setShowDebugGrid(show: boolean): void {
    this.showDebugGrid = show;
    this.saveToStorage('show_debug_grid', show);
  }

  getShowGutter(defaultValue: boolean): boolean {
    return this.showGutter !== null ? this.showGutter : defaultValue;
  }

  setShowGutter(show: boolean): void {
    this.showGutter = show;
    this.saveToStorage('show_gutter', show);
  }

  getShowPageNumbers(defaultValue: boolean): boolean {
    return this.showPageNumbers !== null ? this.showPageNumbers : defaultValue;
  }

  setShowPageNumbers(show: boolean): void {
    this.showPageNumbers = show;
    this.saveToStorage('show_page_numbers', show);
  }
}

